/**
 * ============================================================================
 * usePMTrayQuery — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the PM Tray. Returns three buckets so we
 *          can flip tabs without a re-fetch (DUE_THIS_WEEK / OVERDUE /
 *          RECENTLY_COMPLETED).
 * Endpoint: GET /api/v1/equipment/pm
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  EquipmentCriticality,
  GampClass,
} from '../EquipmentWorkbench/useEquipmentQuery';

export type PMTaskBucket = 'DUE_THIS_WEEK' | 'OVERDUE' | 'RECENTLY_COMPLETED';

export interface PMTask {
  task_id: string;
  equipment_id: string;
  equipment_tag: string;
  equipment_name: string;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  /** Negative when overdue; positive when due in the future. */
  days_until_due: number;
  due_at_utc: string;
  last_pm_at_utc: string | null;
  engineer_hint: string | null;
  bucket: PMTaskBucket;
}

export interface PMTrayResponse {
  items: PMTask[];
  counts: Record<PMTaskBucket, number>;
}

export function usePMTrayQuery(): UseQueryResult<PMTrayResponse> {
  return useQuery<PMTrayResponse>({
    queryKey: ['equipment', 'pmTray'],
    queryFn: ({ signal }) =>
      apiFetch<PMTrayResponse>('/api/v1/equipment/pm', { signal }),
  });
}
